import type { pageMenuIconType } from 'BreetAssets';
import {
	type IDropDownChildArgs,
	type ITeamMembersRole,
	type ITemMemberPermission,
	type IUser,
	type MENUTTITLEYPE,
} from 'BreetHelpers';
import type { CSSProperties, RefObject } from 'react';

export interface ISideMobileSubMenuItem {
	menu: string;
	icon: pageMenuIconType;
	url: string;
	external?: boolean;
	permissions?: ITemMemberPermission | ITemMemberPermission[];
	requiresKyb?: boolean;
	role?: ITeamMembersRole | ITeamMembersRole[];
}

export interface ISideMobileMenuItem {
	menu: MENUTTITLEYPE;
	url: string;
	subMenu?: ISideMobileSubMenuItem[];
	icon?: boolean;
	permissions?: ITemMemberPermission | ITemMemberPermission[];
	requiresKyb?: boolean;
	role?: ITeamMembersRole | ITeamMembersRole[];
}

export const settingsMenu: ISideMobileSubMenuItem[] = [
	{ menu: 'Profile', icon: 'profile', url: '/settings/profile-information' },
	{
		menu: 'Crypto Settings',
		icon: 'crypto-settings',
		url: '/settings/crypto-settings',
		requiresKyb: true,
		role: ['owner', 'super_admin', 'subadmin'],
	},
	{ menu: 'Security', icon: 'security', url: '/settings/security' },
	{
		menu: 'Automatic Settlement',
		icon: 'auto-settlement',
		url: '/settings/automatic-settlement',
		requiresKyb: true,
		role: ['owner', 'super_admin', 'subadmin'],
	},
	// { menu: 'Activity Log', icon: 'activity-log', url: '/settings/activity-log' },
	{
		menu: 'Team Management',
		icon: 'team-management',
		url: '/settings/team-management',
		permissions: 'manage_team',
		requiresKyb: true,
	},
];

export const sideMobileMenuItems: ISideMobileMenuItem[] = [
	{ menu: 'Dashboard', url: '' },
	{ menu: 'Wallet', url: '/wallet' },
	{ menu: 'Addresses', url: '/addresses' },
	{ menu: 'Transactions', url: '/transactions' },
	{ menu: 'Withdrawals', url: '/withdrawals' },
	{ menu: 'Analytics', url: '/analytics' },
	{ menu: 'Notifications', url: '/notifications', icon: true },
	{ menu: 'Settings', url: '/settings', subMenu: settingsMenu, permissions: 'settings' },
	{ menu: 'Developer', url: '/developer', permissions: ['generate_secret_sandbox', 'generate_secret_prod'] },
];

export interface IMobileSubMenus {
	subMenus?: ISideMobileSubMenuItem[];
	dropdownRef: RefObject<HTMLDivElement | null>;
	dropdownStyles: CSSProperties | undefined;
	setMobileMenu: (callBackFn?: () => void | Promise<void>) => () => Promise<void>;
	onMenuClick: (path: string, external?: boolean) => () => void;
	isKybPending: boolean;
	hasAnyPermission: (requiredPermissions: ITemMemberPermission[]) => boolean;
	hasPermission: (requiredPermission: ITemMemberPermission) => boolean;
	userData?: IUser;
}

export interface IMobileMenuItemSub {
	sideMenuItem: ISideMobileMenuItem;
	setMobileMenu: (callBackFn?: () => void | Promise<void>) => () => Promise<void>;
	onMenuClick: (path: string) => () => void;
	dropDownArgs: IDropDownChildArgs;
	isKybPending: boolean;
	hasAnyPermission: (requiredPermissions: ITemMemberPermission[]) => boolean;
	hasPermission: (requiredPermission: ITemMemberPermission) => boolean;
	userData?: IUser;
}

export interface IMobileMenuItem {
	sideMenuItem: ISideMobileMenuItem;
	setMobileMenu: (callBackFn?: () => void | Promise<void>) => () => Promise<void>;
	onMenuClick: (path: string) => () => void;
	isKybPending: boolean;
	hasAnyPermission: (requiredPermissions: ITemMemberPermission[]) => boolean;
	hasPermission: (requiredPermission: ITemMemberPermission) => boolean;
	userData?: IUser;
}

export const filterSideMenuItemPermissions =
	(
		isKybPending: boolean,
		hasAnyPermission: (requiredPermissions: ITemMemberPermission[]) => boolean,
		hasPermission: (permission: ITemMemberPermission) => boolean,
		userData?: IUser
	) =>
	(i: ISideMobileMenuItem | ISideMobileSubMenuItem) => {
		if (i.requiresKyb && isKybPending) return false;

		if (i.permissions) {
			const allowed = Array.isArray(i.permissions) ? hasAnyPermission(i.permissions) : hasPermission(i.permissions);
			if (!allowed) return false;
		}

		if (i.role) {
			const allowedRoles = Array.isArray(i.role) ? i.role : [i.role];
			return userData?.businessRole ? allowedRoles.includes(userData.businessRole) : false;
		}

		return true;
	};
